import { doc, getDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useFirestore, useUser } from "reactfire";
import { createLogger } from "../utils/logger";

const logger = createLogger("useIsAdmin");

/**
 * Hook to check if the current user has admin privileges
 * Returns true if user is authenticated AND has is_admin: true in Firestore
 */
export const useIsAdmin = (): boolean => {
  const { data: user, status: userStatus } = useUser();
  const firestore = useFirestore();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Reset admin status when user changes
    setIsAdmin(false);

    // Don't query if no user or still loading
    if (userStatus === "loading" || !user) {
      return;
    }

    // Query Firestore with proper error handling
    const checkAdminStatus = async () => {
      try {
        const userDoc = doc(firestore, "users", user.uid);
        const docSnap = await getDoc(userDoc);

        if (docSnap.exists()) {
          const userData = docSnap.data();
          setIsAdmin(userData?.is_admin === true);
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        // Silently fail on permission errors - user is not admin
        logger.debug("Unable to check admin status:", error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user, userStatus, firestore]);

  return isAdmin;
};

export default useIsAdmin;
